// How notes refer to their stored pictures and videos. Pure (no DOM, no fetch) so web and mobile share it.

/** The fields of an attachment these helpers need. */
export interface MediaAttachment {
  id: string;
  filename: string | null;
  mime: string | null;
}

/** Pictures and videos stored with a note, by name, for ![alt](attachment:NAME) in its Markdown. */
export type InlineMedia = Record<string, { url: string; video: boolean }>;

/** ![alt](attachment:NAME): a picture or video stored with the note (e.g. the photos of a saved X Article). */
export const ATTACHMENT_IMAGE = /!\[([^\]]*)\]\(attachment:([^)\s]+)\)/g;

/** Types the API will serve inline (see INLINE_TYPES in apps/api/src/media.ts). */
export function isInlineImage(mime: string | null): boolean {
  return ["image/jpeg", "image/png", "image/gif", "image/webp", "image/avif"].includes((mime ?? "").toLowerCase());
}

export function isVideo(mime: string | null): boolean {
  return (mime ?? "").toLowerCase() === "video/mp4";
}

/** An attachment's name without its extension: how Markdown refers to it. */
export function baseName(a: Pick<MediaAttachment, "filename">): string {
  return (a.filename ?? "").replace(/\.[a-z0-9]+$/i, "");
}

/** The attachment names a note's Markdown shows inline (so they aren't repeated in the gallery). */
export function inlineAttachmentNames(source: string): Set<string> {
  return new Set([...source.matchAll(ATTACHMENT_IMAGE)].map((m) => m[2]!));
}

/** Map a note's pictures and videos by name, using `urlFor` to get each one's address. */
export function inlineMedia<A extends MediaAttachment>(attachments: A[], urlFor: (a: A) => string): InlineMedia {
  return Object.fromEntries(
    attachments
      .filter((a) => isInlineImage(a.mime) || isVideo(a.mime))
      .map((a) => [baseName(a), { url: urlFor(a), video: isVideo(a.mime) }]),
  );
}

/** Web clips (source "extension"): the page snapshot images, shown above the text. */
export function isSnapshot(a: Pick<MediaAttachment, "filename" | "mime">): boolean {
  return isInlineImage(a.mime) && /^(Snapshot|Full page)\b/.test(a.filename ?? "");
}
